"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Game = exports.PlayerMode = void 0;
const nanoevents_1 = require("nanoevents");
const Time = require("./Time");
const Loader_1 = require("./Loader");
const Mouse_1 = require("./Input/Mouse");
const Touch_1 = require("./Input/Touch");
const Minimap_1 = require("./Minimap");
const Camera_1 = require("./Graphics/Camera");
const Keyboard_1 = require("./Input/Keyboard");
const SoundSystem_1 = require("./SoundSystem");
const Context_1 = require("./Graphics/Context");
const ReplayPlayer_1 = require("./ReplayPlayer");
const ReplayTrails_1 = require("./ReplayTrails");
const Renderer_1 = require("./Graphics/Renderer");
const SkyScene_1 = require("./Graphics/SkyScene");
const WorldScene_1 = require("./Graphics/WorldScene");
var PlayerMode;
(function (PlayerMode) {
    PlayerMode[PlayerMode["FREE"] = 0] = "FREE";
    PlayerMode[PlayerMode["REPLAY"] = 1] = "REPLAY";
})(PlayerMode || (exports.PlayerMode = PlayerMode = {}));
class Game {
    static init(config) {
        const status = Context_1.Context.checkWebGLSupport();
        if (!status.hasSupport) {
            return {
                status: 'error',
                message: 'No WebGL support!'
            };
        }
        const canvas = document.createElement('canvas');
        if (!canvas) {
            return {
                status: 'error',
                message: 'Failed to create <canvas> element!'
            };
        }
        const context = Context_1.Context.init(canvas);
        if (!context) {
            return {
                status: 'error',
                message: 'Failed to initialize WebGL context'
            };
        }
        const renderer = Renderer_1.Renderer.init(context);
        if (!renderer) {
            return {
                status: 'error',
                message: 'Failed to initialize renderer'
            };
        }
        const worldScene = WorldScene_1.WorldScene.init(context);
        if (!worldScene) {
            return {
                status: 'error',
                message: 'Failed to initialize world scene'
            };
        }
        const skyScene = SkyScene_1.SkyScene.init(context);
        if (!skyScene) {
            return {
                status: 'error',
                message: 'Failed to initialize sky scene'
            };
        }
        const game = new Game({
            canvas,
            config,
            context,
            renderer,
            worldScene,
            skyScene
        });
        return {
            status: 'success',
            game
        };
    }
    setTrailVisible(visible) {
        if (this.replayTrails)
            this.replayTrails.trailVisible = visible;
    }
    setTrailSize(size) {
        if (this.replayTrails)
            this.replayTrails.trailSize = size;
    }
    setPlayerMarkerModel(model) {
        if (this.replayTrails)
            this.replayTrails.playerMarkerModel = model;
    }
    constructor(params) {
        this.pauseTime = 0;
        this.isPaused = false;
        this.lastTime = 0;
        this.accumTime = 0;
        this.timeStep = 1 / 60;
        this.title = '';
        this.speed = '';
        this.fps = '';
        this.buttons = '';
        this.strafes = '';
        this.gravity = true;
        this.isSpeedVisible = true;
        this.isFpsVisible = false;
        this.isButtonsVisible = false;
        this.isStrafesVisible = false;
        this.mouseSensitivity = 1.0;
        this.pointerLocked = false;
        this.touch = new Touch_1.Touch();
        this.mouse = new Mouse_1.Mouse();
        this.keyboard = new Keyboard_1.Keyboard();
        this.entities = [];
        this.minimap = null;
        this.replayTrails = null;
        this.comparisonReplays = [];
        this.activeReplayIndex = 0;
        this.fpsFrameCount = 0;
        this.fpsLastTime = 0;
        this.fpsDisplay = null;
        this.hudReplay = null;
        this.hudFrame = -1;
        this.onLoadAll = (loader) => {
            if (loader && loader.replay) {
                this.clearComparisonReplays();
                this.changeReplay(loader.replay.data);
                this.povReplay = this.player;
                this.addComparisonReplay(loader.replay.data.data.header.name || loader.replay.name, this.player);
                this.changeMode(PlayerMode.REPLAY);
            }
            if (!loader.map || !loader.map.data) {
                return;
            }
            const map = loader.map.data;
            const skies = loader.skies;
            let skiesValid = true;
            skies.forEach(sky => {
                skiesValid = skiesValid && sky.isDone();
            });
            if (skiesValid) {
                skies.forEach(sky => (sky.data ? map.skies.push(sky.data) : 0));
            }
            Object.entries(loader.sprites).forEach(([name, item]) => {
                if (item.data) {
                    map.sprites[name] = item.data;
                }
            });
            if (loader.sounds.length > 0) {
                loader.sounds.forEach(sound => {
                    if (sound.data) {
                        this.sounds.push(sound.data);
                    }
                });
            }
            this.changeMap(map);
            this.setupMinimap(map);
            this.events.emit('load', loader);
        };
        this.draw = () => {
            requestAnimationFrame(this.draw);
            if (!this.fpsDisplay) {
                this.fpsDisplay = document.getElementById('fps-counter');
                this.fpsLastTime = performance.now();
            }
            const canvas = this.canvas;
            const parent = canvas.parentElement;
            if (parent) {
                const pw = parent.clientWidth;
                const ph = parent.clientHeight;
                if (canvas.width !== pw || canvas.height !== ph) {
                    canvas.width = pw;
                    canvas.height = ph;
                    this.camera.aspect = canvas.clientWidth / canvas.clientHeight;
                    this.camera.updateProjectionMatrix();
                    this.context.gl.viewport(0, 0, this.context.gl.drawingBufferWidth, this.context.gl.drawingBufferHeight);
                }
                if (canvas.clientWidth !== canvas.width ||
                    canvas.clientHeight !== canvas.height) {
                    canvas.width = canvas.clientWidth;
                    canvas.height = canvas.clientHeight;
                    this.camera.aspect = canvas.clientWidth / canvas.clientHeight;
                    this.camera.updateProjectionMatrix();
                    this.context.gl.viewport(0, 0, this.context.gl.drawingBufferWidth, this.context.gl.drawingBufferHeight);
                }
            }
            const currTime = Time.now() / 1000;
            if (this.lastTime !== 0) {
                const dt = Math.min(currTime - this.lastTime, 0.1);
                this.accumTime += Math.max(0, dt);
                while (this.accumTime >= this.timeStep) {
                    this.update(this.timeStep);
                    this.accumTime -= this.timeStep;
                }
            }
            this.renderer.draw();
            if (this.mapName !== '') {
                this.skyScene.draw(this.camera);
                this.worldScene.draw(this.camera, this.entities);
            }
            if (this.replayTrails) {
                this.replayTrails.draw(this.camera, this.mode === PlayerMode.REPLAY ? this.povReplay : null);
            }
            if (this.minimap) {
                const currentFrames = new Map();
                for (const entry of this.comparisonReplays) {
                    currentFrames.set(entry.replay, entry.replay.currentFrame);
                }
                this.minimap.drawPlayer(this.camera.position[0], this.camera.position[1], this.replayTrails, currentFrames, this.mode === PlayerMode.REPLAY ? this.povReplay : null);
            }
            this.lastTime = currTime;
            this.fpsFrameCount++;
            const now = performance.now();
            if (now - this.fpsLastTime >= 1000) {
                if (this.fpsDisplay) {
                    this.fpsDisplay.textContent = `${this.fpsFrameCount} FPS`;
                }
                this.fpsFrameCount = 0;
                this.fpsLastTime = now;
            }
        };
        this.onTouchStart = (e) => {
            const touch = e.touches.item(0);
            if (touch) {
                this.touch.pressed = true;
                this.touch.position[0] = touch.clientX;
                this.touch.position[1] = touch.clientY;
            }
        };
        this.onTouchEnd = () => {
            this.touch.pressed = false;
            this.touch.delta[0] = 0;
            this.touch.delta[1] = 0;
        };
        this.onTouchMove = (e) => {
            const touch = e.touches.item(0);
            if (touch && this.touch.pressed) {
                this.touch.delta[0];
                this.touch.delta[0] = touch.clientX - this.touch.position[0];
                this.touch.delta[1] = touch.clientY - this.touch.position[1];
                this.touch.position[0] = touch.clientX;
                this.touch.position[1] = touch.clientY;
            }
        };
        this.onMouseMove = (e) => {
            if (this.pointerLocked) {
                if (this.mode === PlayerMode.FREE) {
                    const sensitivity = this.mouseSensitivity * 0.5;
                    this.camera.rotation[0] = Math.min(Math.max(this.camera.rotation[0] + (e.movementY * sensitivity) / 100, -Math.PI / 2), Math.PI / 2);
                    this.camera.rotation[1] -= (e.movementX * sensitivity) / 100;
                }
                this.mouse.position[0] = e.pageX;
                this.mouse.position[1] = e.pageY;
            }
        };
        this.keyDown = (e) => {
            this.keyboard.keys[e.which] = 1;
            if (this.pointerLocked) {
                e.preventDefault();
                return false;
            }
            return true;
        };
        this.keyUp = (e) => {
            this.keyboard.keys[e.which] = 0;
            if (this.pointerLocked) {
                e.preventDefault();
                return false;
            }
            return true;
        };
        this.onVisibilityChange = () => {
            if (document.hidden) {
                if (this.isPaused) {
                    return;
                }
                this.pauseTime = Time.now() / 1000;
                this.isPaused = true;
            }
            else {
                if (!this.isPaused) {
                    return;
                }
                this.lastTime = Time.now() / 1000 - this.pauseTime + this.lastTime;
                this.isPaused = false;
            }
        };
        this.sounds = [];
        this.soundSystem = new SoundSystem_1.SoundSystem();
        this.config = params.config;
        this.loader = new Loader_1.Loader(this.config);
        this.loader.events.on('loadall', this.onLoadAll);
        document.addEventListener('touchstart', this.onTouchStart, false);
        document.addEventListener('touchend', this.onTouchEnd, false);
        document.addEventListener('touchcancel', this.onTouchEnd, false);
        document.addEventListener('touchmove', this.onTouchMove, false);
        document.addEventListener('mousemove', this.onMouseMove, false);
        window.addEventListener('keydown', this.keyDown);
        window.addEventListener('keyup', this.keyUp);
        window.addEventListener('visibilitychange', this.onVisibilityChange);
        this.canvas = params.canvas;
        this.camera = Camera_1.Camera.init(this.canvas.width / this.canvas.height);
        this.context = params.context;
        this.renderer = params.renderer;
        this.worldScene = params.worldScene;
        this.skyScene = params.skyScene;
        this.mode = PlayerMode.FREE;
        this.player = new ReplayPlayer_1.ReplayPlayer(this);
        this.povReplay = this.player;
        this.events = (0, nanoevents_1.createNanoEvents)();
        this.mapName = '';
    }
    addComparisonReplay(name, replay) {
        if (!this.replayTrails) {
            this.replayTrails = new ReplayTrails_1.ReplayTrails(this.context);
        }
        const entry = this.replayTrails.addEntry(name, replay);
        this.comparisonReplays.push(entry);
        if (replay !== this.player && this.player.replay && replay.replay) {
            const duration = replay.replay.data.time / 1000;
            const percent = duration > 0 ? (this.player.currentTime / duration) * 100 : 0;
            replay.seekByPercent(percent, false);
            if (this.player.isPlaying) {
                replay.play();
                if (this.player.isPaused)
                    replay.pause();
            }
        }
        if (this.comparisonReplays.length > 1) {
            this.changeMode(PlayerMode.REPLAY);
            this.povReplay.applyCamera();
        }
        this.updateLegend();
        return entry;
    }
    removeComparisonReplay(entry) {
        var _a;
        const idx = this.comparisonReplays.indexOf(entry);
        if (idx !== -1) {
            this.comparisonReplays.splice(idx, 1);
            (_a = this.replayTrails) === null || _a === void 0 ? void 0 : _a.removeEntry(entry);
            if (this.activeReplayIndex >= this.comparisonReplays.length) {
                this.activeReplayIndex = Math.max(0, this.comparisonReplays.length - 1);
            }
            this.updateLegend();
        }
    }
    clearComparisonReplays() {
        var _a;
        this.comparisonReplays.length = 0;
        (_a = this.replayTrails) === null || _a === void 0 ? void 0 : _a.clear();
        this.activeReplayIndex = 0;
        this.povReplay = this.player;
        this.updateLegend();
    }
    resetComparisonReplays() {
        this.clearComparisonReplays();
        if (this.player.replay) {
            const name = this.player.replay.data.header.name || 'Primary replay';
            this.addComparisonReplay(name, this.player);
        }
    }
    addComparisonReplayFromUrl(name, url) {
        return __awaiter(this, void 0, void 0, function* () {
            const replay = yield this.loader.loadReplayDataFromUrl(url);
            const player = new ReplayPlayer_1.ReplayPlayer(this);
            player.changeReplay(replay);
            return this.addComparisonReplay(name, player);
        });
    }
    setActiveReplay(index) {
        if (index >= 0 && index < this.comparisonReplays.length) {
            this.activeReplayIndex = index;
            this.povReplay = this.comparisonReplays[index].replay;
            this.povReplay.focusCameraOnCurrentFrame();
            this.setPlayerTitle(this.povReplay);
            this.updatePovOverlay();
            this.changeMode(PlayerMode.REPLAY);
            this.updateLegend();
        }
    }
    playAll() {
        const duration = this.getTimelineDuration();
        if (duration > 0 && this.getTimelineTime() >= duration) {
            this.seekAllByPercent(0);
        }
        this.player.play();
        for (const entry of this.comparisonReplays) {
            if (entry.replay !== this.player) {
                entry.replay.play();
            }
        }
    }
    pauseAll() {
        this.player.pause();
        for (const entry of this.comparisonReplays) {
            if (entry.replay !== this.player) {
                entry.replay.pause();
            }
        }
    }
    speedUpAll() {
        this.player.speedUp();
        for (const entry of this.comparisonReplays)
            entry.replay.speed = this.player.speed;
    }
    speedDownAll() {
        this.player.speedDown();
        for (const entry of this.comparisonReplays)
            entry.replay.speed = this.player.speed;
    }
    toggleCameraMode() {
        if (this.mode === PlayerMode.FREE) {
            this.changeMode(PlayerMode.REPLAY);
            this.povReplay.applyCamera();
        }
        else {
            this.changeMode(PlayerMode.FREE);
            this.pauseAll();
        }
    }
    cyclePov(direction) {
        if (this.comparisonReplays.length === 0)
            return;
        const count = this.comparisonReplays.length;
        const next = (this.activeReplayIndex + direction + count) % count;
        this.setActiveReplay(next);
    }
    toggleMinimap() {
        var _a;
        (_a = this.minimap) === null || _a === void 0 ? void 0 : _a.toggleExpanded();
    }
    getTimelineDuration() {
        const players = this.comparisonReplays.length > 0
            ? this.comparisonReplays.map(entry => entry.replay)
            : [this.player];
        return players.reduce((longest, replay) => Math.max(longest, replay.replay ? replay.replay.data.time / 1000 : 0), 0);
    }
    getTimelineTime() {
        const players = this.comparisonReplays.length > 0
            ? this.comparisonReplays.map(entry => entry.replay)
            : [this.player];
        return players.reduce((current, replay) => Math.max(current, replay.currentTime), 0);
    }
    isPlaybackPlaying() {
        const players = this.comparisonReplays.length > 0
            ? this.comparisonReplays.map(entry => entry.replay)
            : [this.player];
        return players.some(replay => replay.isPlaying && !replay.isPaused);
    }
    seekAllByPercent(value) {
        const inReplayMode = this.mode === PlayerMode.REPLAY;
        const sharedTime = this.getTimelineDuration() * Math.max(0, Math.min(value, 100)) / 100;
        const players = this.comparisonReplays.length > 0
            ? this.comparisonReplays.map(entry => entry.replay)
            : [this.player];
        for (const replay of players) {
            if (!replay.replay)
                continue;
            const duration = replay.replay.data.time / 1000;
            replay.seekByPercent(duration > 0 ? (sharedTime / duration) * 100 : 0, false);
        }
        if (inReplayMode) {
            this.povReplay.applyCamera();
        }
    }
    seekAllBySeconds(seconds) {
        const duration = this.getTimelineDuration();
        const percent = duration > 0 ? ((this.getTimelineTime() + seconds) / duration) * 100 : 0;
        this.seekAllByPercent(percent);
    }
    enableFreeRoamComparison() {
        this.changeMode(PlayerMode.FREE);
    }
    updateLegend() {
        const legend = document.getElementById('replay-legend');
        if (!legend)
            return;
        legend.innerHTML = '';
        this.comparisonReplays.forEach((entry, i) => {
            const r = Math.round(entry.color[0] * 255);
            const g = Math.round(entry.color[1] * 255);
            const b = Math.round(entry.color[2] * 255);
            const div = document.createElement('div');
            div.className = 'legend-item';
            div.innerHTML = `<span class="legend-dot" style="background:rgb(${r},${g},${b})"></span><span class="legend-name">${entry.name}</span>`;
            div.style.cssText = `
        display:flex; align-items:center; gap:6px; min-width:0; padding:5px 7px;
        border:1px solid rgba(255,255,255,.22); border-radius:4px;
        background:rgba(0,0,0,.76); color:#f1f5f9; cursor:pointer;
        font:600 11px/1.2 system-ui,sans-serif;
      `;
            const dot = div.querySelector('.legend-dot');
            if (dot) {
                dot.style.cssText += ';display:block;flex:0 0 8px;width:8px;height:8px;border-radius:999px;box-shadow:0 0 0 1px rgba(255,255,255,.35)';
            }
            const label = div.querySelector('.legend-name');
            if (label) {
                label.style.cssText = 'min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
            }
            if (i === this.activeReplayIndex) {
                div.style.outline = '1px solid #fff';
            }
            div.addEventListener('click', event => {
                event.stopPropagation();
                this.setActiveReplay(i);
            });
            legend.appendChild(div);
        });
    }
    getCanvas() {
        return this.canvas;
    }
    load(name) {
        this.events.emit('loadstart');
        this.loader.load(name);
    }
    loadFromPath(filePath) {
        return __awaiter(this, void 0, void 0, function* () {
            this.events.emit('loadstart');
            yield this.loader.loadFromPath(filePath);
        });
    }
    changeMap(map) {
        if (this.mapName.toLowerCase() === map.name.toLowerCase()) {
            return;
        }
        this.mapName = map.name;
        this.worldScene.changeMap(map);
        this.skyScene.changeMap(map);
        this.entities = map.entities;
        const spawn = map.entities.find(e => e['classname'] === 'info_player_start');
        if (spawn) {
            this.camera.position[0] = spawn.origin[0];
            this.camera.position[1] = spawn.origin[1];
            this.camera.position[2] = spawn.origin[2];
        }
        else {
            this.camera.position[0] = 0;
            this.camera.position[1] = 0;
            this.camera.position[2] = 0;
        }
        this.camera.rotation[0] = 0;
        this.camera.rotation[1] = 0;
        this.camera.rotation[2] = 0;
    }
    changeReplay(replay) {
        this.events.emit('prereplaychange', this, replay);
        this.player.changeReplay(replay);
        this.setPlayerTitle(this.player);
        this.events.emit('postreplaychange', this, replay);
    }
    setLocalResourceRoot(path) {
        this.loader.setLocalResourceRoot(path);
    }
    loadReplayFromUrl(url, name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.events.emit('loadstart');
            yield this.loader.loadReplayFromUrl(url, name);
        });
    }
    setPlayerTitle(player) {
        var _a;
        const replayData = (_a = player.replay) === null || _a === void 0 ? void 0 : _a.data;
        if (replayData === null || replayData === void 0 ? void 0 : replayData.header) {
            const header = replayData.header;
            const name = header.name || 'Unknown player';
            const info = header.additionalInfo ? ` — ${header.additionalInfo}` : '';
            this.setTitle(`${name}${info}`);
        }
    }
    updatePovOverlay() {
        var _a, _b;
        const frames = (_b = (_a = this.povReplay.replay) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.frames;
        if (!frames || frames.length === 0)
            return;
        if (this.hudReplay === this.povReplay && this.hudFrame === this.povReplay.currentFrame)
            return;
        this.hudReplay = this.povReplay;
        this.hudFrame = this.povReplay.currentFrame;
        this.setGravity(frames[this.povReplay.currentFrame].gravity);
    }
    changeMode(mode) {
        this.mode = mode;
        this.events.emit('modechange', mode);
        if (mode === PlayerMode.FREE) {
            this.isSpeedVisible = false;
            this.isFpsVisible = false;
            this.isButtonsVisible = false;
            this.isStrafesVisible = false;
        }
        else if (mode === PlayerMode.REPLAY)
            this.isSpeedVisible = true;
    }
    setTitle(title) {
        this.title = title;
        this.events.emit('titlechange', title);
    }
    setSpeed(speed) {
        this.speed = speed;
        this.events.emit('speedchange', speed);
    }
    setFps(fps) {
        this.fps = fps;
        this.events.emit('fpschange', fps);
    }
    setButtons(buttons) {
        this.buttons = buttons;
        this.events.emit('buttonschange', buttons);
    }
    setStrafes(strafes) {
        this.strafes = strafes;
        this.events.emit('strafeschange', strafes);
    }
    setGravity(gravity) {
        this.gravity = gravity;
        let gravityStr = '';
        if (gravity === true)
            gravityStr = 'usp';
        else
            gravityStr = 'knife';
        this.events.emit('gravitychange', gravityStr);
    }
    getTitle() {
        return this.title;
    }
    setupMinimap(map) {
        var _a;
        const root = (_a = document.querySelector('#hlv-target')) === null || _a === void 0 ? void 0 : _a.parentElement;
        if (!root)
            return;
        if (!this.minimap) {
            this.minimap = new Minimap_1.Minimap(root);
        }
        this.minimap.updateMap(map);
        if (!this.replayTrails) {
            this.replayTrails = new ReplayTrails_1.ReplayTrails(this.context);
        }
    }
    update(dt) {
        this.events.emit('preupdate', this);
        const camera = this.camera;
        const keyboard = this.keyboard;
        const mouse = this.mouse;
        const touch = this.touch;
        if (this.mode === PlayerMode.FREE) {
            if (this.touch.pressed) {
                camera.rotation[0] = Math.min(Math.max(camera.rotation[0] + touch.delta[1] / 100, -Math.PI / 2), Math.PI / 2);
                camera.rotation[1] -= touch.delta[0] / 100;
            }
            else {
            }
            const speed = 500;
            const ds = speed * dt;
            const KEY_W = Keyboard_1.Keyboard.KEYS.W;
            const KEY_S = Keyboard_1.Keyboard.KEYS.S;
            const KEY_A = Keyboard_1.Keyboard.KEYS.A;
            const KEY_D = Keyboard_1.Keyboard.KEYS.D;
            const downKey = Keyboard_1.Keyboard.KEYS.CTRL;
            const upKey = Keyboard_1.Keyboard.KEYS.SHIFT;
            if (keyboard.keys[KEY_W] !== keyboard.keys[KEY_S]) {
                if (keyboard.keys[KEY_W]) {
                    camera.position[1] -= Math.cos(camera.rotation[1] + Math.PI / 2) * ds;
                    camera.position[0] += Math.sin(camera.rotation[1] + Math.PI / 2) * ds;
                }
                else if (keyboard.keys[KEY_S]) {
                    camera.position[1] -= Math.cos(camera.rotation[1] - Math.PI / 2) * ds;
                    camera.position[0] += Math.sin(camera.rotation[1] - Math.PI / 2) * ds;
                }
            }
            if (keyboard.keys[KEY_A] !== keyboard.keys[KEY_D]) {
                if (keyboard.keys[KEY_A]) {
                    camera.position[1] += Math.cos(camera.rotation[1]) * ds;
                    camera.position[0] -= Math.sin(camera.rotation[1]) * ds;
                }
                else if (keyboard.keys[KEY_D]) {
                    camera.position[1] -= Math.cos(camera.rotation[1]) * ds;
                    camera.position[0] += Math.sin(camera.rotation[1]) * ds;
                }
            }
            if (keyboard.keys[upKey] !== keyboard.keys[downKey]) {
                if (keyboard.keys[upKey]) {
                    camera.position[2] += ds;
                }
                else if (keyboard.keys[downKey]) {
                    camera.position[2] -= ds;
                }
            }
        }
        mouse.delta[0] = 0;
        mouse.delta[1] = 0;
        const inReplayMode = this.mode === PlayerMode.REPLAY;
        this.player.update(dt, false, this.player === this.povReplay);
        for (const entry of this.comparisonReplays) {
            if (entry.replay === this.player)
                continue;
            entry.replay.speed = this.player.speed;
            entry.replay.update(dt, false, entry.replay === this.povReplay);
        }
        if (inReplayMode) {
            this.povReplay.applyCamera();
        }
        this.updatePovOverlay();
        if (this.replayTrails) {
            for (const entry of this.comparisonReplays) {
                this.replayTrails.updateEntry(entry, entry.replay.currentFrame);
            }
        }
        this.events.emit('postupdate', this);
    }
}
exports.Game = Game;
